import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr, API_BASE, getStoredToken } from "@/lib/api";
import { ImagePlus, Loader2, Trash2, Camera, Upload, X, Images } from "lucide-react";

const fileUrl = (path) => `${API_BASE}/files/${path}?auth=${encodeURIComponent(getStoredToken())}`;

export default function ProjectPhotos({ projectId }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const camRef = useRef();
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const { data } = useQuery({
    queryKey: ["project-photos", projectId],
    queryFn: async () => (await api.get(`/projects/${projectId}/photos`)).data,
  });
  const photos = data?.photos || [];

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data: r } = await api.post(`/projects/${projectId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      qc.setQueryData(["project-photos", projectId], { photos: r.photos });
      toast.success(`Added ${r.added} photo${r.added > 1 ? "s" : ""}`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/projects/${projectId}/photos/${id}`);
      qc.setQueryData(["project-photos", projectId], { photos: photos.filter((p) => p.id !== id) });
      toast.success("Photo removed");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="project-photos">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl font-bold tracking-tight flex items-center gap-2"><Images className="h-5 w-5 text-primary" /> Project Photos</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Attach site, reference or progress photos to this project.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="photos-take" onClick={() => camRef.current?.click()} disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:bg-secondary disabled:opacity-60">
            <Camera className="h-4 w-4" /> Take photo
          </button>
          <button data-testid="photos-upload" onClick={() => fileRef.current?.click()} disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-bold hover:bg-orange-600 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload photos
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid="photos-file-input" onChange={(e) => upload(e.target.files)} />
          <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files)} />
        </div>
      </div>

      {photos.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-border py-24 text-center text-muted-foreground" data-testid="photos-empty">
          <ImagePlus className="h-10 w-10 mx-auto mb-3 opacity-60" />
          <p className="font-semibold">No photos yet</p>
          <p className="text-sm">Upload multiple site or reference photos to keep them with the project.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photos-grid">
          {photos.map((p) => (
            <div key={p.id} className="group relative rounded-xl border border-border bg-card overflow-hidden" data-testid={`photo-${p.id}`}>
              <button onClick={() => setLightbox(p)} className="block w-full">
                <img src={fileUrl(p.path)} alt={p.name} className="w-full h-40 object-cover group-hover:scale-[1.02] transition-transform" />
              </button>
              <button data-testid={`photo-delete-${p.id}`} onClick={() => remove(p.id)}
                className="absolute top-2 right-2 rounded-full bg-black/60 text-white p-1.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-background/90 backdrop-blur flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
          <button className="absolute top-5 right-5 rounded-full border border-border bg-card p-2 hover:bg-secondary" onClick={() => setLightbox(null)}><X className="h-5 w-5" /></button>
          <div onClick={(e) => e.stopPropagation()} className="max-w-4xl w-full">
            <img src={fileUrl(lightbox.path)} alt={lightbox.name} className="w-full max-h-[80vh] object-contain rounded-xl border border-border" />
            <div className="mt-3 text-center text-sm text-muted-foreground">{lightbox.name}</div>
          </div>
        </div>
      )}
    </div>
  );
}
